import React from 'react';

interface ProgressBarProps {
  progress: number; // 0-100
  className?: string;
  textClassName?: string;
}

/**
 * ProgressBar - A reusable progress bar component.
 *
 * Displays a visual progress indicator with percentage text.
 * Used throughout the application to show user progress through the booking flow.
 *
 * @param progress - Progress percentage (0-100)
 * @param className - Optional CSS class for styling
 *
 * @example
 * ```tsx
 * <ProgressBar progress={45} />
 * <ProgressBar progress={63} className="mt-8" />
 * ```
 */
const ProgressBar: React.FC<ProgressBarProps> = ({ progress, className = '', textClassName = 'progress-text' }) => {
  // Use 5% for visual rendering when progress is 0, but show 0% in text
  const visualProgress = progress === 0 ? 5 : progress;
  const displayText = progress; // Always show the actual progress value in text

  return (
    <div 
      className={`progress-bar-container progress-bar-isolated ${className}`} 
      style={{ 
        position: 'relative',
        width: '100%'
      }}
    >
      <div 
        className="progress-bar-outer" 
        role="progressbar" 
        aria-label="Progress" 
        aria-valuenow={progress} 
        aria-valuemin={0} 
        aria-valuemax={100}
        style={{
          width: '100%',
          height: '16px',
          backgroundColor: '#e0e0e0',
          borderRadius: '8px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          className="progress-bar-inner" 
          style={{ 
            width: `${visualProgress}%`,
            height: '100%',
            backgroundColor: '#e69e6d',
            borderRadius: '8px',
            transition: 'width 0.3s ease'
          }} 
        />
      </div>
      <div 
        className={textClassName} 
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '65%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#333',
          fontSize: '0.8rem',
          fontWeight: '600',
          pointerEvents: 'none'
        }}
      >
        {displayText}%
      </div>
    </div>
  );
};

export default ProgressBar;
